<template>
	<div class="social-auth-box" :class="$store.state.app.platform === 'ios' ? 'ios' : ''">
		<button type="button" class="button facebook" @click.prevent="facebookAuth">
			<span class="inner"><i
				class="fa-brands fa-facebook-f"></i>{{ message }} {{ $t('auth.via_facebook') }}</span>
		</button>
		<button type="button" class="button google" @click.prevent="googleAuth">
			<span class="inner"><i class="fa-brands fa-google"></i>{{ message }} {{ $t('auth.via_google') }}</span>
		</button>
		<button type="button" class="button apple" @click.prevent="appleAuth"
		        v-if="$store.state.app.platform === 'ios'">
			<span class="inner"><i class="fa-brands fa-apple"></i>{{ message }} {{ $t('auth.via_apple') }}</span>
		</button>
	</div>
</template>

<script>
import {SocialLogin} from '@capgo/capacitor-social-login';

export default {
	name: "SocialAuthBox",
	props: ['type'],
	data() {
		return {
			googleClient: null,
		}
	},
	computed: {
		message() {
			if (this.type === 'login') {
				return this.$t('auth.log_in');
			}

			return this.$t('auth.sign_up');
		}
	},
	methods: {
		async authProvider(provider, options = {}, tokenExtractor) {
			const status = await SocialLogin.isLoggedIn({provider});

			if (status.isLoggedIn) {
				await SocialLogin.logout({provider});
			}

			try {
				const data = await SocialLogin.login({provider, options});
				const {token, requestAccessToken} = tokenExtractor(data);

				this.handleSocialResult(provider, token, requestAccessToken);
			} catch (error) {
				console.log(error);
			}
		},

		handleSocialResult(provider, token, requestAccessToken = false) {
			if (this.type === 'login') {
				this.socialLogin(provider, token, requestAccessToken);
			} else {
				this.socialRegister(provider, token, requestAccessToken);
			}
		},

		appleAuth() {
			this.authProvider(
				'apple',
				{scopes: ['email', 'name']},
				data => ({
					token: data.result.accessToken?.token
				})
			);
		},

		facebookAuth() {
			this.authProvider(
				'facebook',
				{
					permissions: ['email', 'public_profile'],
					limitedLogin: true
				},
				data => ({
					token: data.result.accessToken?.token || data.result.idToken
				})
			);
		},

		googleAuth() {
			if (this.$store.state.app.native) {
				this.googleNativeAuth();
			} else {
				this.googleWebAuth();
			}
		},

		googleWebAuth() {
			if (!this.googleClient) {
				this.googleClient = google.accounts.oauth2.initTokenClient({
					client_id: process.env.VUE_APP_GOOGLE_WEB_ID,
					scope: 'email profile',
					ux_mode: 'popup',
					callback: (response) => {
						this.handleSocialResult('google', response.access_token);
					},
				});
			}

			this.googleClient.requestAccessToken();
		},

		googleNativeAuth() {
			this.authProvider(
				'google',
				{
					scopes: ['email', 'profile'],
					forceRefreshToken: true,
				},
				data => {
					let token = data.result?.accessToken?.token;
					let requestAccessToken = false;

					if (!token) {
						token = data.result.serverAuthCode;
						requestAccessToken = true;
					}

					return {token, requestAccessToken};
				}
			);
		},

		socialLogin(driver, token, requestAccessToken = false) {
			const afterLoginAction = this.$store.state.auth.afterLoginAction;
			const payload = {
				token,
				driver,
				...(requestAccessToken && {request_access_token: true}),
				...(this.$store.state.app.native && {
					app_version: this.$store.state.app.version
				}),
				...(afterLoginAction && { after_login_action: afterLoginAction }),
			};

			this.$root.$emit('socials-login-loading-status', true);

			this.axios.post('/api/social-login', payload)
				.then(response => {
					this.$store.dispatch('processLogin', response);
					this.$root.$emit('socials-login', false);
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: 'Oops',
						text: error.response.data.message
					});
					this.$root.$emit('socials-login-loading-status', false);
				});
		},

		socialRegister(driver, token, requestAccessToken = false) {
			const payload = {
				token,
				driver,
				...(requestAccessToken && {request_access_token: true})
			};

			this.axios.post('/api/get-socials-data', payload)
				.then(response => {
					const res = response.data.data;

					const data = {
						first_name: res.first_name,
						last_name: res.last_name,
						email: res.email,
						[`${driver}_token`]:
							driver === 'google' && requestAccessToken
								? res.token
								: res.token || token
					};

					this.$root.$emit('socials-data-received', data);
				})
				.catch(() => {
					this.$swal.fire({
						icon: 'error',
						title: 'Oops',
						text: this.$t('general.default_error')
					});
				});
		}
	}
}
</script>

<style scoped lang="scss">
.registration-popup-box {
	.social-auth-box {
		flex-direction: row;

		.button {
			//width: calc(50% - 10px);
			margin-right: 20px;
			margin-bottom: 0;
			flex: 1;

			&:last-child {
				margin-right: 0;
			}
		}
	}

	.social-auth-box.ios {
		flex-direction: column;
		width: 100%;

		.button {
			margin-right: 0;
			margin-bottom: 10px;
			width: 100%;

			&:last-child {
				margin-bottom: 0;
			}
		}
	}

	@media($tabletL) {
		.social-auth-box {
			flex-direction: column;
			width: 100%;

			.button {
				margin-right: 0;
				margin-bottom: 10px;
				width: 100%;

				&:last-child {
					margin-bottom: 0;
				}
			}
		}
	}


}

.social-auth-box {
	display: flex;
	flex-direction: column;

	.button {
		width: 100%;
		color: var(--white-color, $whiteColor);
		margin-bottom: 10px;
		position: relative;

		.inner {
			position: static;
		}

		i {
			position: absolute;
			left: 15px;
		}

		&:last-child {
			margin-bottom: 0;
		}

		&:hover {
			background: var(--white-color, $whiteColor);
			color: var(--background-color, $backgroundColor);
		}
	}

	.facebook {
		background: #1658A5;
	}

	.google {
		background: #C22E2E;
	}

	.apple {
		color: $blackColor;
		background: $whiteColor;

		i {
			font-size: 18px;
			top: calc(50% - 9px);
		}

		&:hover {
			color: $whiteColor;
			background: $blackColor;
		}
	}
}

@media($mobileM) {
	.social-auth-box {
		.button {
			font-size: 14px;
		}
	}
}

@media($mobileS) {
	.social-auth-box {
		.button {
			font-size: 12px;
		}
	}
}

</style>